import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { useApolloClient } from '@apollo/client';
import { GET_S3_PRESIGNED_URL } from '@/graphql/queries';
import ValidationError from 'components/consumer/ValidationError';
import DropzoneUploadWrapper from 'components/consumer/Dropzone/DropzoneUploadWrapper';
import DropzoneUploadLabel from 'components/consumer/Dropzone/DropzoneUploadLabel';
import { MAX_TRADE_FILES, MAX_TRADE_TOTAL_SIZE, TRADE_FILE_ACCEPT, TRADE_FILE_ACCEPT_OBJECT } from '../constants';
const TradeFormFileUpload = t0 => {
  const $ = _c(27);
  const {
    onUrlsChange,
    errorMessage
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [fileEntries, setFileEntries] = useState(t1);
  const [dropError, setDropError] = useState(null);
  const client = useApolloClient();
  let t2;
  let t3;
  if ($[1] !== fileEntries || $[2] !== onUrlsChange) {
    t2 = () => {
      onUrlsChange(fileEntries.map(_temp).filter(Boolean));
    };
    t3 = [fileEntries, onUrlsChange];
    $[1] = fileEntries;
    $[2] = onUrlsChange;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== client) {
    t4 = async file => {
      const presigned = await client.query({
        query: GET_S3_PRESIGNED_URL,
        fetchPolicy: "network-only",
        variables: {
          key: file.name,
          section: "Trade_Application",
          contentType: file.type
        }
      });
      if (!presigned?.data?.getS3presignedUrl?.success) {
        throw new Error("Could not get upload URL");
      }
      const uploadUrl = presigned.data.getS3presignedUrl.url;
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type
        }
      });
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      return uploadUrl.split("?")[0];
    };
    $[5] = client;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const uploadToS3 = t4;
  const getDropRejectionError = _temp4;
  let t5;
  if ($[7] !== fileEntries || $[8] !== uploadToS3) {
    t5 = async (accepted, rejected_0) => {
      const ACCEPTED_MIME_TYPES = TRADE_FILE_ACCEPT.split(",");
      const [acceptedWithValidMime, acceptedWithInvalidMime] = accepted.reduce((t6, file_0) => {
        const [ok, bad] = t6;
        if (ACCEPTED_MIME_TYPES.includes(file_0.type)) {
          return [[...ok, file_0], bad];
        }
        return [ok, [...bad, file_0]];
      }, [[], []]);
      const allRejected = [...(rejected_0 || []), ...acceptedWithInvalidMime];
      if (allRejected.length > 0) {
        setDropError(getDropRejectionError(allRejected));
        if (acceptedWithValidMime.length === 0) {
          return;
        }
      } else {
        setDropError(null);
      }
      if (acceptedWithValidMime.length + fileEntries.length > MAX_TRADE_FILES) {
        setDropError(`You can upload up to ${MAX_TRADE_FILES} files.`);
        return;
      }
      const currentTotalSize = fileEntries.reduce(_temp5, 0);
      const incomingSize = acceptedWithValidMime.reduce(_temp6, 0);
      const maxSizeMB = MAX_TRADE_TOTAL_SIZE / 1048576;
      if (currentTotalSize + incomingSize > MAX_TRADE_TOTAL_SIZE) {
        setDropError(`Total upload size exceeds ${maxSizeMB} MB. Please upload smaller files.`);
        return;
      }
      const newEntries = acceptedWithValidMime.map(_temp7);
      setFileEntries(prev => [...prev, ...newEntries]);
      await Promise.all(newEntries.map(async entry_0 => {
        ;
        try {
          const url = await uploadToS3(entry_0.file);
          setFileEntries(prev_1 => prev_1.map(f_1 => f_1.id === entry_0.id ? {
            ...f_1,
            uploading: false,
            url
          } : f_1));
        } catch (t7) {
          setFileEntries(prev_0 => prev_0.map(f_0 => f_0.id === entry_0.id ? {
            ...f_0,
            uploading: false,
            uploadError: "Upload failed. Please try again."
          } : f_0));
        }
      }));
    };
    $[7] = fileEntries;
    $[8] = uploadToS3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onDrop = t5;
  let t6;
  if ($[10] !== uploadToS3) {
    t6 = async fileEntry => {
      setFileEntries(prev_2 => prev_2.map(f_2 => f_2.id === fileEntry.id ? {
        ...f_2,
        uploading: true,
        uploadError: null
      } : f_2));
      ;
      try {
        const url_0 = await uploadToS3(fileEntry.file);
        setFileEntries(prev_4 => prev_4.map(f_4 => f_4.id === fileEntry.id ? {
          ...f_4,
          uploading: false,
          url: url_0
        } : f_4));
      } catch (t7) {
        setFileEntries(prev_3 => prev_3.map(f_3 => f_3.id === fileEntry.id ? {
          ...f_3,
          uploading: false,
          uploadError: "Upload failed. Please try again."
        } : f_3));
      }
    };
    $[10] = uploadToS3;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const retryUpload = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = fileEntry_0 => {
      setFileEntries(prev_5 => prev_5.filter(f_5 => f_5.id !== fileEntry_0.id));
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const removeFile = t7;
  const canAddMore = fileEntries.length < MAX_TRADE_FILES;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-sm text-gray-light1 mb-3"><strong>Accepted file types: JPG, PNG, PDF.</strong> Upload your business license for tax-exempt approval. If you do not have a website or Instagram, please attach photos of recent projects.</p>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== fileEntries || $[15] !== retryUpload) {
    t9 = fileEntries.length > 0 && <ul className="mb-3 list-none p-0 space-y-1">{fileEntries.map(entry_1 => <li key={entry_1.id} className="flex items-center text-sm gap-2"><strong className="truncate max-w-[200px] text-brand">{entry_1.file.name}</strong>{entry_1.uploading && <span className="text-gray-light1 text-xs">Uploading…</span>}{entry_1.url && <span className="text-gray-light1 text-xs">Uploaded</span>}{entry_1.uploadError && <><span className="text-red text-xs" role="alert">{entry_1.uploadError}</span><button type="button" className="text-xs text-brand underline hover:no-underline" onClick={() => retryUpload(entry_1)}>Retry</button></>}{!entry_1.uploading && <button type="button" className="ml-auto text-gray-light1 hover:text-black" onClick={() => removeFile(entry_1)} aria-label={`Remove ${entry_1.file.name}`}>✕</button>}</li>)}</ul>;
    $[14] = fileEntries;
    $[15] = retryUpload;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== canAddMore || $[18] !== onDrop) {
    t10 = canAddMore && <Dropzone onDrop={onDrop} accept={TRADE_FILE_ACCEPT_OBJECT} multiple={true}>{_temp8}</Dropzone>;
    $[17] = canAddMore;
    $[18] = onDrop;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== dropError || $[21] !== errorMessage) {
    t11 = (dropError || errorMessage) && <ValidationError block={true}>{dropError || errorMessage}</ValidationError>;
    $[20] = dropError;
    $[21] = errorMessage;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <div className="mb-4">{t8}{t9}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
TradeFormFileUpload.propTypes = {
  onUrlsChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};
export default TradeFormFileUpload;
function _temp(f) {
  return f.url;
}
function _temp2(e) {
  return e.code === "file-invalid-type";
}
function _temp3(item) {
  if (item.errors) {
    return item.errors.some(_temp2);
  }
  return !TRADE_FILE_ACCEPT.split(",").includes(item.type);
}
function _temp4(rejected) {
  const plural = rejected.length > 1 ? "s" : "";
  const hasWrongType = rejected.some(_temp3);
  if (hasWrongType) {
    return `Invalid file type${plural}. Only JPG, PNG, and PDF files are accepted.`;
  }
  return "Some files could not be added. Accepted types: JPG, PNG, PDF.";
}
function _temp5(sum, entry) {
  return sum + (entry.file?.size || 0);
}
function _temp6(sum_0, file_1) {
  return sum_0 + file_1.size;
}
function _temp7(file_2) {
  return {
    id: `${file_2.name}-${file_2.lastModified}-${Math.floor(Math.random() * 1000000000)}`,
    file: file_2,
    uploading: true,
    url: null,
    uploadError: null
  };
}
function _temp8(t0) {
  const {
    getRootProps,
    getInputProps
  } = t0;
  return <DropzoneUploadWrapper><div {...getRootProps()}><input {...getInputProps()} /><DropzoneUploadLabel allowMultiple={true} allowVideos={false} /></div></DropzoneUploadWrapper>;
}